import { MixinInterface } from "./MixinInterface";

// Type definition for a constructor that creates classes implementing MixinInterface
export type MixinClass = new (...args: any[]) => MixinInterface;

export class MixinUtil {
    public static applyMixins(target: any, mixins: MixinClass[]) {
        for (const mixin of mixins) {
            this.applyMixin(target, mixin);
        }
    }

    static applyMixin(target: any, mixin: MixinClass) {
        let mixinName = mixin.name;
        const mixinObject = new mixin();

        // Use the simple initialize() method from MixinInterface instead of complex initialization
        mixinName = mixinObject.name;
        // Create a true inheritance chain by extending the prototype
        this.createTrueInheritanceChain(target, mixin, mixinName);

        // Store the initialize method for the target to call in its constructor
        this.addMixinInitializer(target, mixinObject);
    }

    private static createTrueInheritanceChain(target: any, mixin: any, mixinName: string) {
        // Create a new prototype that inherits from the current target prototype
        // and adds the mixin's methods
        const mixinProto = mixin.prototype;

        // Get all properties from the mixin's prototype chain
        const properties = this.getAllProperties(mixinProto);

        for (const propName of properties) {
            if (propName === 'constructor') continue; // Skip constructor

            const propDescriptor = Object.getOwnPropertyDescriptor(mixinProto, propName);
            if (propDescriptor) {
                this.inheritPropertyWithSuper(target, propName, propDescriptor);
            }
        }
    }

    private static inheritPropertyWithSuper(target: any, propName: string, propDescriptor: PropertyDescriptor) {
        const existingDescriptor = Object.getOwnPropertyDescriptor(target.prototype, propName);

        if (!existingDescriptor) {
            // Property doesn't exist on target, add it directly from mixin
            Object.defineProperty(target.prototype, propName, {
                ...propDescriptor,
                enumerable: propDescriptor.enumerable !== false,
                configurable: true
            });
        } else {
            // Property exists - create a super method that the component can call
            const superMethodName = `super_${propName}`;

            if (typeof propDescriptor.value === 'function') {
                // For methods, create a callable super method
                Object.defineProperty(target.prototype, superMethodName, {
                    value: function(...args: any[]) {
                        return propDescriptor.value.apply(this, args);
                    },
                    writable: false,
                    enumerable: false,
                    configurable: true
                });
            } else if (propDescriptor.get || propDescriptor.set) {
                // For getters/setters, create super accessors
                if (propDescriptor.get) {
                    Object.defineProperty(target.prototype, `super_get_${propName}`, {
                        value: function() {
                            return propDescriptor.get.call(this);
                        },
                        writable: false,
                        enumerable: false,
                        configurable: true
                    });
                }

                if (propDescriptor.set) {
                    Object.defineProperty(target.prototype, `super_set_${propName}`, {
                        value: function(value: any) {
                            return propDescriptor.set.call(this, value);
                        },
                        writable: false,
                        enumerable: false,
                        configurable: true
                    });
                }
            }

            // The target's existing property/method remains as-is (target wins conflicts)
        }
    }

    private static getAllProperties(obj: any): string[] {
        const props = new Set<string>();
        let current = obj;
        while (current && current !== Object.prototype && current !== Function.prototype) {
            Object.getOwnPropertyNames(current).forEach(prop => {
                if (prop !== 'constructor') {
                    props.add(prop);
                }
            });
            current = Object.getPrototypeOf(current);
        }
        return Array.from(props);
    }

    private static addMixinInitializer(target: any, mixinObject: MixinInterface) {
        // Store the mixin's initialize method to be called by the target constructor
        if (!target._mixinInitializers) {
            target._mixinInitializers = [];
        }

        // Store the mixin's initialize method
        target._mixinInitializers.push(mixinObject.initialize.bind(mixinObject));

        // Also collect listener definitions from the mixin
        this.addMixinListenerDefs(target, mixinObject);
    }

    // Simple method for components to call in their constructor to initialize all mixins
    public static initializeMixins(instance: any) {
        const constructor = instance.constructor;
        if (constructor._mixinInitializers) {
            for (const initializer of constructor._mixinInitializers) {
                // Call each mixin's initialize method with the component instance as context
                initializer.call(instance);
            }
        }
    }

    private static addMixinListenerDefs(target: any, mixinObject: MixinInterface) {
        // Check if the mixin has listenerDefs
        if (typeof mixinObject.listenerDefs === 'function' || mixinObject.listenerDefs) {
            if (!target._mixinListenerDefs) {
                target._mixinListenerDefs = {};
            }

            // Get the listener definitions from the mixin
            const mixinListenerDefs = typeof mixinObject.listenerDefs === 'function'
                ? mixinObject.listenerDefs()
                : mixinObject.listenerDefs;

            // Merge them into the target's collection
            if (mixinListenerDefs) {
                Object.assign(target._mixinListenerDefs, mixinListenerDefs);
            }
        }
    }

    // Method for components to get all collected listener definitions from mixins
    public static getMixinListenerDefs(target: any): any {
        const constructor = typeof target === 'function' ? target : target.constructor;
        return constructor._mixinListenerDefs ?? {};
    }
}
